import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useT } from '../i18n';
import { getApiClient } from '../api/factory';
import {
  createConversationTag,
  deleteConversationTag,
  listConversationTags,
  updateConversationTag,
  type ConversationTag,
} from '../api/conversationTags';
import { ApiError } from '../api/client';

// ANX-043 — Админская секция в /settings: CRUD тегов диалогов.
// Видна только operator с role=admin. Не отдельный route, а блок на
// существующей странице — TenantSecurityPolicySection идёт по тому же
// паттерну (admin-only guard внутри компонента).

const PALETTE = [
  '#0EA5E9', // sky
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#64748B', // slate
  '#14B8A6', // teal
] as const;

export function ConversationTagsAdmin() {
  const role = useAuthStore((s) => s.session?.operator.role);
  const t = useT();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(PALETTE[0]);
  const [error, setError] = useState<string | null>(null);

  const { data: tags } = useQuery({
    queryKey: ['conversation_tags'] as const,
    queryFn: () => listConversationTags(getApiClient()),
    enabled: role === 'admin',
  });

  const createMut = useMutation({
    mutationFn: (input: { name: string; color: string }) =>
      createConversationTag(getApiClient(), input),
    onSuccess: () => {
      setName('');
      setColor(PALETTE[0]);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['conversation_tags'] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  const updateMut = useMutation({
    mutationFn: (input: { id: string; name: string; color: string }) =>
      updateConversationTag(getApiClient(), input.id, { name: input.name, color: input.color }),
    onSuccess: () => {
      setEditingId(null);
      setName('');
      setColor(PALETTE[0]);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['conversation_tags'] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteConversationTag(getApiClient(), id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversation_tags'] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  if (role !== 'admin') return null;

  function startEdit(tag: ConversationTag) {
    setEditingId(tag.id);
    setName(tag.name);
    setColor(tag.color);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setName('');
    setColor(PALETTE[0]);
    setError(null);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim() === '') {
      setError(t('tags.error.nameRequired'));
      return;
    }
    const action = editingId
      ? updateMut.mutateAsync({ id: editingId, name: name.trim(), color })
      : createMut.mutateAsync({ name: name.trim(), color });
    action.catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('tags.error.nameTaken'));
      } else {
        setError(t('common.unknownError'));
      }
    });
  }

  function handleDelete(tag: ConversationTag) {
    if (!window.confirm(t('tags.confirmDelete') + ' ' + tag.name + '?')) return;
    deleteMut.mutate(tag.id);
  }

  return (
    <section className="mt-6 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{t('tags.adminTitle')}</h3>
      <p className="mb-3 text-xs text-slate-500">{t('tags.adminHint')}</p>

      <ul className="mb-4 flex flex-col gap-1.5">
        {(tags ?? []).map((tag) => (
          <li
            key={tag.id}
            className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          >
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: tag.color }}
                aria-hidden="true"
              />
              <span className="font-medium">{tag.name}</span>
              <span className="font-mono text-[11px] text-slate-400">{tag.color}</span>
            </span>
            <span className="flex gap-1">
              <button
                type="button"
                onClick={() => startEdit(tag)}
                aria-label={t('common.edit')}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <Pencil size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(tag)}
                aria-label={t('common.delete')}
                disabled={deleteMut.isPending}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </span>
          </li>
        ))}
        {tags && tags.length === 0 && (
          <li className="text-xs italic text-slate-500">{t('tags.empty')}</li>
        )}
      </ul>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder={t('tags.namePlaceholder')}
          aria-label={t('tags.namePlaceholder')}
          maxLength={40}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-1 focus:ring-accent-200"
        />
        <div role="radiogroup" aria-label={t('tags.colorLabel')} className="flex gap-1">
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={c === color}
              onClick={() => setColor(c)}
              aria-label={c}
              className={
                'h-6 w-6 rounded-full border-2 ' +
                (c === color ? 'border-slate-900' : 'border-transparent')
              }
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <button
          type="submit"
          disabled={createMut.isPending || updateMut.isPending}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {editingId ? t('common.save') : t('common.create')}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={cancelEdit}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            {t('common.cancel')}
          </button>
        )}
      </form>
      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
